import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, ExternalLink } from 'lucide-react';
import { getAppBySlug } from '@/apps/registry';
import NotFound from './NotFound';

// Generic per-app host, mounted at /:slug (e.g. /wwd). Looks the app up by path slug and renders
// a themed header. HMH Watch (status 'live') currently offers an "Open" button to its own app;
// its in-shell view via the BFF proxy at /hmhwatch lands in the next step. Dummy apps show a
// "preview" placeholder.
export default function AppHost() {
  const { slug } = useParams();
  const app = getAppBySlug(slug);
  if (!app) return <NotFound />;

  const Icon = app.Icon;
  const isLive = app.status === 'live';

  return (
    <div className="stagger">
      <Link to="/" className="mb-6 inline-flex items-center gap-1 text-sm text-muted hover:text-text">
        <ArrowLeft className="h-4 w-4" /> All apps
      </Link>

      <div className="mb-6 flex items-center gap-4">
        <span className="inline-flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-(--radius-md) border border-border bg-surface-elevated text-accent">
          <Icon className="h-7 w-7" aria-hidden />
        </span>
        <div>
          <h1 className="font-(family-name:--font-heading) text-2xl md:text-3xl font-bold text-text">
            {app.name}
          </h1>
          <p className="text-muted">{app.blurb}</p>
        </div>
      </div>

      <div className="bg-surface border border-border rounded-(--radius-md) p-8">
        {isLive ? (
          <div className="max-w-xl">
            <p className="text-muted">
              HMH Watch runs as its own application today. Open it below — its seamless in-shell
              view (reverse-proxied at this path, no second login) lands in the next step.
            </p>
            {app.externalUrl && (
              <a
                href={app.externalUrl}
                target="_blank"
                rel="noreferrer"
                className="mt-4 inline-flex items-center gap-2 rounded-(--radius-sm) bg-accent px-4 py-2 text-sm font-semibold text-inverse hover:bg-accent-hover"
              >
                Open HMH Watch <ExternalLink className="h-4 w-4" aria-hidden />
              </a>
            )}
          </div>
        ) : (
          <>
            <span className="eyebrow">Preview</span>
            <p className="mt-3 max-w-xl text-muted">
              <span className="text-text font-medium">{app.name}</span> is a placeholder app in the
              framework. It will render mock {app.blurb.toLowerCase()} once its module lands.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
